import { JSONModel } from '@/store/models/jsonModel';
import Image from '@/store/models/image';

export interface BannerStandOrderItemJSON {
  id: number;
  product_id: number;
  product?: {
    id: number;
    title: string;
  };
  banner_stand_image_id: number;
  banner_stand_image?: {
    id: number;
    images?: Array<{
      id: number;
      thumbnail: string;
      preview: string;
      s3: string;
      uri: string;
    } | null>;
  };
}

export interface BannerStandOrderJSON {
  id: number;
  practice_id: number;
  user_id: number;
  location_id?: number;
  delivery_address?: string;
  status: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  practice?: {
    id: number;
    title: string;
  };
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    title?: string;
  };
  location?: {
    id: number;
    title: string;
  };
  items?: BannerStandOrderItemJSON[];
}

export class BannerStandOrderItem {
  id: number = 0;
  productId: number = 0;
  productTitle: string = '';
  bannerStandImageId: number = 0;
  image?: {
    id: number;
    thumbnail: string;
    preview: string;
    s3: string;
    uri?: string;
  };

  static fromJSON(json: BannerStandOrderItemJSON): BannerStandOrderItem {
    const item = new BannerStandOrderItem();
    item.id = json.id;
    item.productId = json.product_id;
    item.productTitle = json.product?.title || '';
    item.bannerStandImageId = json.banner_stand_image_id;
    
    // Get first image from images array
    const firstImage = json.banner_stand_image?.images?.[0];
    if (firstImage) {
      // Use thumbnail, fallback to uri if thumbnail is incomplete
      const thumbnailUrl = firstImage.thumbnail && firstImage.thumbnail.length > 30 
        ? firstImage.thumbnail 
        : firstImage.uri || firstImage.s3 || '';
      
      item.image = {
        id: firstImage.id,
        thumbnail: thumbnailUrl,
        preview: firstImage.preview || thumbnailUrl,
        s3: firstImage.s3 || '',
      };
    }
    
    return item;
  }
}

export default class BannerStandOrder extends JSONModel<BannerStandOrderJSON> {
  id: number = 0;
  practiceId: number = 0;
  practiceName: string = '';
  userId: number = 0;
  userName: string = '';
  userEmail: string = '';
  locationId: number = 0;
  locationName: string = '';
  deliveryAddress: string = '';
  status: string = 'pending';
  notes: string = '';
  createdAt: string = '';
  updatedAt: string = '';
  items: BannerStandOrderItem[] = [];

  public fromJSON(json: BannerStandOrderJSON): void {
    this.id = json.id;
    this.practiceId = json.practice_id;
    this.userId = json.user_id;
    this.locationId = json.location_id || 0;
    this.status = json.status;
    this.notes = json.notes || '';
    this.createdAt = json.created_at;
    this.updatedAt = json.updated_at;

    if (json.practice) {
      this.practiceName = json.practice.title;
    }

    if (json.user) {
      this.userName = `${json.user.first_name} ${json.user.last_name}`;
      this.userEmail = json.user.email;
    }

    // Use delivery_address from API (computed by backend)
    if (json.delivery_address) {
      this.deliveryAddress = json.delivery_address;
    }

    if (json.location) {
      this.locationName = json.location.title || '';
    }

    if (json.items && Array.isArray(json.items)) {
      this.items = json.items.map(item => BannerStandOrderItem.fromJSON(item));
    }
  }

  public toJSON(): BannerStandOrderJSON {
    return {
      id: this.id,
      practice_id: this.practiceId,
      user_id: this.userId,
      location_id: this.locationId,
      status: this.status,
      notes: this.notes,
      created_at: this.createdAt,
      updated_at: this.updatedAt,
    };
  }
}
